import { useState, useEffect } from "react";
import moment from "moment";
import APIVariables from "../../../../services/api-variables";
import Axios from "../../../../services/Axios";
import { handleCatch } from "../../../../shared/helpers";
import BrochureLink from "../../Brochure/BrochureLink";
import MahButton from "./MahButton";
import MahLogo from "./MahLogo";
import Modal from "react-modal";

const dispenseDateModalContentClass =
  "fixed left-1/2 top-1/2 z-[5] -translate-x-1/2 -translate-y-1/2 outline-none " +
  "!w-[37%] max-[850px]:!w-[70%] rounded-[30px] bg-white";

const overlayClassName = "fixed inset-0 z-[4] bg-black/20";

function MahSelection({
  receivedForm,
  openFinishModal,
  onPrevious,
  setFinishModalContent,
  dispensePAF,
  pafID,
  setModalNonCompliantPAFWarning,
  product_id,
  setIsOperationInProgress,
  isOperationInProgress,
  hcp,
}) {
  const [mahs, setMahs] = useState([]);
  const [validateScreenFields, setValidateScreenFields] = useState({});
  const [showErrors, setShowErrors] = useState(false);
  const [form, setForm] = useState({
    prescription_id: pafID,
    marketing_holder_id: null,
    confirmation_risk_1: false,
    confirmation_risk_2: false,
    indication_offLabel: null,
  });

  const [indication_offlabel, setIndication_offlabel] = useState(null);
  const [modalDisplay, setModalDisplay] = useState(null);
  const [showDispenseDateModal, setShowDispenseDateModal] = useState(false);
  const [dispenseDate, setDispenseDate] = useState(moment().format("YYYY-MM-DD"));
  const [creationDate, setCreationDate] = useState(() =>
    receivedForm?.pfa_issued
      ? moment(receivedForm.pfa_issued).utc().format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD")
  );

  // update creationDate when receivedForm is provided/changes
  useEffect(() => {
    if (receivedForm?.pfa_issued) {
      const parsed = moment(receivedForm.pfa_issued);
      setCreationDate(parsed.isValid() ? parsed.utc().format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"));
    }
  }, [receivedForm]);

  const [amendedDispenseDate, setAmendedDispenseDate] = useState(null);
  const [pendingMahId, setPendingMahId] = useState(null);

  const findMarketingHolder = mahs.find(
    (element) => element.id === form.marketing_holder_id
  );
  const checkofflabel =
    !!findMarketingHolder &&
    findMarketingHolder?.indication_ids?.includes(receivedForm.indication_id);

  useEffect(() => {
    setIndication_offlabel(checkofflabel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkofflabel]);

  useEffect(() => {
    getMAHs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const clickComplete = () => {
    const areAllFields = Object.values(validateScreenFields).every((field) => {
      return field.isValid;
    });

    if (
      areAllFields &&
      indication_offlabel === false &&
      modalDisplay === false
    ) {
      setModalDisplay(true);
      setModalNonCompliantPAFWarning((prevState) => ({
        ...prevState,
        svg: true,
        isOpen: true,
        title: "Continue with off-license use",
        description:
          "You have selected an indication that is not officially approved for use for this Marketing Authorisation Holder. Are you sure you want to proceed?",
      }));
    } else if (areAllFields) {
      // show the dispense date modal so user can confirm/change date
      setPendingMahId(form.marketing_holder_id);
      // prefer backend pfa_issued when available, otherwise today
      setCreationDate(
        receivedForm?.pfa_issued
          ? moment(receivedForm.pfa_issued).utc().format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD")
      );
      setDispenseDate(moment().format("YYYY-MM-DD"));
      setShowDispenseDateModal(true);
    } else {
      setShowErrors(true);
    }
  };

  const validateForm = () => {
    const { marketing_holder_id, confirmation_risk_1, confirmation_risk_2 } =
      form;

    setValidateScreenFields({
      marketing_holder_id: {
        isValid: !!marketing_holder_id,
      },
      confirmation_risk_1: {
        isValid: confirmation_risk_1 === true,
      },
      confirmation_risk_2: {
        isValid: confirmation_risk_2 === true,
      },
    });
  };

  const getMAHs = async () => {
    await Axios.get(APIVariables.MAHS + "/" + product_id)
      .then((res) => {
        const { status, data } = res;

        if (status === 200) {
          setMahs(data);
        }
      })
      .catch((error) => {
        handleCatch(error);
      });
  };
  const handleChange = (e) => {
    if (e.target?.checked !== undefined) {
      const { checked, name } = e.target;
      setForm((prevState) => ({ ...prevState, [name]: checked }));
    } else {
      setForm((prevState) => ({ ...prevState, marketing_holder_id: e }));
    }
  };

  const handleDispenseDateConfirm = () => {
    // allow null: if user didn't change nominated date, send amended_dispensed_date = null
    const raw = amendedDispenseDate;
    let amendedISO = null;

    if (raw) {
      // validate only when user provided a date
      if (!moment(raw, "YYYY-MM-DD", true).isValid()) {
        return alert("Please enter a valid nominated dispensing date.");
      }
      if (moment(raw).isAfter(moment(), "day")) {
        return alert("Nominated dispensing date cannot be in the future.");
      }

      amendedISO = moment(raw, "YYYY-MM-DD")
        .format("YYYY-MM-DDTHH:mm:ss[Z]");

      setForm((prev) => ({
        ...prev,
        marketing_holder_id: pendingMahId || prev.marketing_holder_id,
        dispensing_date: raw,
      }));
    } else {
      // user left it unchanged -> don't overwrite dispensing_date, only ensure MAH is set
      setForm((prev) => ({
        ...prev,
        marketing_holder_id: pendingMahId || prev.marketing_holder_id,
      }));
    }

    setFinishModalContent({
      title: "Prescription Authorisation Form Completed",
      content: "The PAF has been completed and can be dispensed.",
      onFinish: () => {
        if (isOperationInProgress) return;
        setIsOperationInProgress(true);
        dispensePAF({
          ...form,
          marketing_holder_id: pendingMahId || form.marketing_holder_id,
          // amended_dispensed_date will be null when user didn't change it
          amended_dispensed_date: amendedISO,
        })
          .then(() => setIsOperationInProgress(false))
          .catch((error) => {
            console.error("Error:", error);
            setIsOperationInProgress(false);
          });
      },
    });

    openFinishModal();
    setShowDispenseDateModal(false);
    setModalDisplay(false);
  };

  const brandName = hcp?.name ?? "this product";
  const risk1Invalid =
    showErrors && !validateScreenFields?.confirmation_risk_1?.isValid;
  const risk2Invalid =
    showErrors && !validateScreenFields?.confirmation_risk_2?.isValid;

  return (
    <div
      className="relative z-2 mx-auto w-full rounded-[10px] bg-white px-6 py-5 shadow-[0_0_40px_rgba(0,0,0,0.2)] sm:px-10"
      data-testid="paf-mah-selection"
    >
      <div className="flex w-full flex-col gap-4">
        <div className="text-lg text-[#00567a]" data-testid="paf-mah-step-label">
          Step 3/3
        </div>
        <div
          className="mt-5 text-lg font-semibold text-[#00567a]"
          data-testid="paf-mah-title"
        >
          MAH Selection*:
        </div>
        <div className="text-base text-[#646464]" data-testid="paf-mah-description">
          Please select the designated MAH in which you are dispensing
          medication from:
        </div>

        <div className="relative w-full" data-testid="paf-mah-options">
          {showErrors && !validateScreenFields.marketing_holder_id?.isValid && (
            <span
              className="mb-2 block text-sm font-light italic text-[#e41818]"
              data-testid="paf-mah-error-required"
            >
              Required
            </span>
          )}
          <div className="flex flex-wrap items-center justify-center gap-4">
            {mahs.map((mah, index) => {
              return !!mah.logo ? (
                <MahLogo
                  src={mah.logo}
                  alt={mah.name}
                  key={`mah_logo_${index}`}
                  selected={mah.id === form.marketing_holder_id}
                  data-testid={`paf-mah-option-${mah.id}`}
                  onClick={() => {
                    handleChange(mah.id);
                    setModalDisplay(false);
                  }}
                />
              ) : (
                <MahButton
                  text={mah.name}
                  key={`mah_button_${mah.name}_${index}`}
                  data-testid={`paf-mah-option-${mah.id}`}
                  onClick={() => {
                    handleChange(mah.id);
                    setModalDisplay(false);
                  }}
                  selected={mah.id === form.marketing_holder_id}
                />
              );
            })}
          </div>
        </div>

        <div className="mt-5 w-full" data-testid="paf-mah-confirmation-risk-1-section">
          <label
            htmlFor="confirmation_risk_1"
            className={
              "flex cursor-pointer items-start gap-3 text-base leading-snug text-[#646464] " +
              (risk1Invalid ? "text-[#e41818]" : "")
            }
          >
            <input
              type="checkbox"
              id="confirmation_risk_1"
              name="confirmation_risk_1"
              checked={!!form.confirmation_risk_1}
              className="mt-1 h-5 w-5 shrink-0 rounded border border-[#00567a] accent-[#00567a]"
              onChange={handleChange}
              data-testid="paf-mah-confirmation-risk-1"
            />
            <span>
              I confirm that I am ordering on behalf of a registered pharmacy
              and that {brandName} will be dispensed in accordance with the risk
              minimisation procedures for {brandName}, as specified by
              <b className={risk1Invalid ? "text-[#e41818]" : ""}>
                {" " +
                  (mahs?.find((mah) => mah.id === form.marketing_holder_id)
                    ?.full_name || "")}{" "}
              </b>
              in the{" "}
              <BrochureLink
                className={risk1Invalid ? "text-[#e41818]" : ""}
                hcp={hcp}
              />
              .
            </span>
          </label>
        </div>

        <div className="mt-2 w-full" data-testid="paf-mah-confirmation-risk-2-section">
          <label
            htmlFor="confirmation_risk_2"
            className={
              "flex cursor-pointer items-start gap-3 text-base leading-snug text-[#646464] " +
              (risk2Invalid ? "text-[#e41818]" : "")
            }
          >
            <input
              type="checkbox"
              id="confirmation_risk_2"
              name="confirmation_risk_2"
              checked={!!form.confirmation_risk_2}
              className="mt-1 h-5 w-5 shrink-0 rounded border border-[#00567a] accent-[#00567a]"
              onChange={handleChange}
              data-testid="paf-mah-confirmation-risk-2"
            />
            <span>
              I confirm that treatment lengths will be limited to a maximum of 4
              weeks supply for women of childbearing potential and a maximum of
              12 weeks for males and women of non-childbearing potential
              patients. For women of child bearing potential dispensing will be
              within 7 days of the date of prescription.
            </span>
          </label>
        </div>

        <div
          className="mt-6 flex flex-wrap gap-8 border-t border-[#e8e8e8] pt-6"
          data-testid="paf-mah-pharmacist-details"
        >
          <div className="w-full min-w-50 sm:w-1/4">
            <label className="text-sm font-normal text-[#00567a]">
              Pharmacist name:
            </label>
            <input
              type="text"
              className="mt-1 w-full border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] text-lg leading-[22px] text-[#646464] outline-none"
              value={receivedForm?.approved_or_rejected_by}
              readOnly
              disabled
              data-testid="paf-mah-pharmacist-name"
            />
          </div>
          <div className="w-full min-w-50 sm:w-1/4">
            <label className="text-sm font-normal text-[#00567a]">Date:</label>
            <div
              className="mt-1 text-lg leading-[22px] text-[#646464]"
              data-testid="paf-mah-date"
            >
              {moment().format("DD / MM / YYYY")}
            </div>
          </div>
        </div>

        <div
          className="flex flex-col gap-6 pt-10 sm:flex-row sm:items-center sm:justify-between"
          data-testid="paf-mah-actions"
        >
          <button
            type="button"
            className="cursor-pointer border-none bg-transparent p-0 text-base text-[#8c8c8c] hover:text-[#3c3c3c]"
            onClick={() => onPrevious()}
            data-testid="paf-mah-previous-button"
          >
            Previous
          </button>
          <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div
              className="text-center text-base leading-[19px] text-[#00567a] sm:text-left"
              data-testid="paf-mah-mandatory-field-note"
            >
              *symbol indicating a mandatory field
            </div>
            <button
              type="button"
              className="inline-flex h-[46px] min-w-[134px] cursor-pointer items-center justify-center rounded-full border-0 bg-[#00567a] px-6 text-lg font-bold leading-[22px] text-white hover:bg-[#005eb8] sm:min-w-[160px]"
              onClick={() => clickComplete()}
              data-testid="paf-mah-complete-button"
            >
              Complete
            </button>
          </div>
        </div>
      </div>

      {/* Dispense Date Modal */}
      {showDispenseDateModal && (
        <Modal
          isOpen={showDispenseDateModal}
          onRequestClose={() => setShowDispenseDateModal(false)}
          className={dispenseDateModalContentClass}
          contentLabel="Dispensing date of PAF"
          overlayClassName={overlayClassName}
          shouldCloseOnOverlayClick={false}
        >
          <div
            className="grid min-h-[200px] gap-4 p-8 text-center sm:p-10"
            data-testid="paf-mah-dispense-date-modal"
          >
            <div
              className="text-[30px] font-bold leading-[37px] text-[#00567a]"
              data-testid="paf-mah-dispense-date-title"
            >
              Dispensing date of PAF
            </div>
            <div className="text-left text-base leading-5 text-[#646464]">
              Please confirm the{" "}
              <span className="underline">dispensing date of this PAF</span> and
              ensure that it accurately reflects the relevant dates
            </div>
            <div className="text-left text-base leading-5 text-[#646464]">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-[#00567a]">Creation Date:</label>
                  <input
                    type="date"
                    value={creationDate}
                    max={moment().format("YYYY-MM-DD")}
                    onChange={(e) => setCreationDate(e.target.value)}
                    className="mt-1 rounded border border-[#c8c8c8] p-2 text-[#646464]"
                    disabled
                    data-testid="paf-mah-creation-date-input"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-[#00567a]">Dispensing Date:</label>
                  <input
                    type="date"
                    value={dispenseDate}
                    max={moment().format("YYYY-MM-DD")}
                    onChange={(e) => setDispenseDate(e.target.value)}
                    className="mt-1 rounded border border-[#c8c8c8] p-2 text-[#646464]"
                    disabled
                    data-testid="paf-mah-dispense-date-input"
                  />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <p className="text-base text-[#00567a]">
                    If the dispensing date is incorrect, please enter the nominated
                    dispensing date:
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-[#00567a]">
                    Nominated Dispensing Date (If different to *Dispensing Date):
                  </label>
                  <input
                    type="date"
                    value={amendedDispenseDate || ""}
                    max={moment().format("YYYY-MM-DD")}
                    onChange={(e) => setAmendedDispenseDate(e.target.value)}
                    className="mt-1 rounded border border-[#c8c8c8] p-2 text-[#646464]"
                    data-testid="paf-mah-nominated-dispense-date-input"
                  />
                </div>
              </div>
            </div>
          </div>
          <div
            className="mb-10 flex flex-wrap items-center justify-center gap-4 px-8 pb-8"
            data-testid="paf-mah-dispense-date-actions"
          >
            <button
              type="button"
              className="mr-0 cursor-pointer border-none bg-transparent text-base font-bold leading-[19px] text-[#646464] hover:text-[#646464c9] sm:mr-6"
              onClick={() => setShowDispenseDateModal(false)}
              data-testid="paf-mah-dispense-date-cancel-button"
            >
              Cancel
            </button>
            <button
              type="button"
              className="h-[46px] w-[159px] cursor-pointer rounded-full border-0 bg-[#00a03b] text-base font-bold leading-[19px] text-white hover:bg-[#009639]"
              onClick={handleDispenseDateConfirm}
              data-testid="paf-mah-dispense-date-confirm-button"
            >
              Confirm
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default MahSelection;
